/**
 * Dictate Mode Service - Speech-to-Text for Chat Composer
 * 
 * Records audio from microphone, detects silence to auto-stop,
 * and sends audio to /api/dictate for Whisper transcription.
 * 
 * States: idle, recording, processing
 * 
 * Supports both web (MediaRecorder) and native (expo-av) platforms.
 */

import { Platform, AppState } from 'react-native';
import * as FileSystem from 'expo-file-system';
import { dictateApi } from './api';
import { devLog } from '@/lib/devLog';

// Web recording uses MediaRecorder and never needs expo-av. Loading the
// deprecated native module eagerly made every web/SSR build evaluate it and
// emit a warning even though no native recorder could run there.
const Audio = Platform.OS === 'web' ? null : require('expo-av').Audio;
// Hardcoded numeric values to avoid enum export inconsistencies on native builds.
const INTERRUPTION_MODE_IOS_DUCK_OTHERS = 2;
const INTERRUPTION_MODE_ANDROID_DUCK_OTHERS = 2;

// Constants
const SILENCE_TIMEOUT_MS = 2500; // Auto-stop after sustained silence once speech is heard
const AUDIO_SAMPLE_RATE_WEB = 16000; // Whisper native rate, fine for web MediaRecorder
const AUDIO_SAMPLE_RATE_NATIVE = 44100; // iOS/Android native rate — 16kHz is not reliably supported by the AAC encoder
const SILENCE_THRESHOLD = 0.02; // RMS amplitude threshold for silence detection (0–1)
const MAX_RECORDING_DURATION_MS = 5 * 60 * 1000; // 5 minutes — still well under Whisper's 25MB limit at AAC 128kbps
const MIN_RECORDING_BEFORE_SILENCE_MS = 1200; // Ignore leading silence / mic warmup
const SILENCE_CHECK_INTERVAL_MS = 100;
// Both platforms emit the same shape to the waveform: a rolling history of
// RMS amplitudes (oldest first). 40 samples at 100ms = the last 4 seconds.
const LEVEL_HISTORY_SIZE = 40;

export const DICTATE_NOTICES = {
  NO_SPEECH: 'no_speech',
  TOO_SHORT: 'too_short',
};

export const DICTATE_STATES = {
  IDLE: 'idle',
  STARTING: 'starting',
  RECORDING: 'recording',
  PROCESSING: 'processing',
};

export { MAX_RECORDING_DURATION_MS };

/**
 * Dictate Mode Service
 * Manages microphone recording and transcription for chat input.
 */
export class DictateModeService {
  constructor() {
    // State
    this.state = DICTATE_STATES.IDLE;
    
    // Web recording
    this.mediaRecorder = null;
    this.audioChunks = [];
    this.stream = null;
    this.audioContext = null;
    this.analyser = null;
    
    // Native recording (expo-av)
    this.nativeRecording = null;
    this.recordingUri = null;
    
    // Silence detection / auto-stop
    this.silenceTimeout = null;
    this.lastSoundTime = null;
    this.hasHeardSound = false;
    this.recordingStartedAt = null;
    this.silenceCheckInterval = null;
    this.maxDurationTimeout = null;
    
    // Native metering
    this.nativeMeteringInterval = null;

    // Optional: elapsed-ms ticker for UI countdown
    this.onDurationTick = null;
    this.durationTickInterval = null;
    
    // Cancellation flag — when true, stopRecording skips transcription
    this._cancelled = false;
    
    // Callbacks
    this.onStateChange = null;
    this.onTranscript = null;
    this.onError = null;
    // Non-error outcomes the user still needs to hear about (nothing was
    // said, clip too short). Receives a DICTATE_NOTICES value.
    this.onNotice = null;
    this.onAudioLevel = null;

    // The app can keep several composers mounted in the iOS navigation
    // back-stack. Track which button owns the active session so a hidden
    // composer cannot receive another composer's state/transcript or stop it
    // when that hidden screen later unmounts.
    this.owner = null;
    this.sessionGeneration = 0;
    this.stateListeners = new Set();
  }

  /**
   * Configure callbacks
   */
  configure(callbacks = {}, owner = null) {
    this.owner = owner;
    if (Object.prototype.hasOwnProperty.call(callbacks, 'onStateChange')) {
      this.onStateChange = callbacks.onStateChange;
    }
    if (Object.prototype.hasOwnProperty.call(callbacks, 'onTranscript')) {
      this.onTranscript = callbacks.onTranscript;
    }
    if (Object.prototype.hasOwnProperty.call(callbacks, 'onError')) {
      this.onError = callbacks.onError;
    }
    if (Object.prototype.hasOwnProperty.call(callbacks, 'onNotice')) {
      this.onNotice = callbacks.onNotice;
    }
    if (Object.prototype.hasOwnProperty.call(callbacks, 'onAudioLevel')) {
      this.onAudioLevel = callbacks.onAudioLevel;
    }
    if (Object.prototype.hasOwnProperty.call(callbacks, 'onDurationTick')) {
      this.onDurationTick = callbacks.onDurationTick;
    }
  }

  getRecordingStartedAt() {
    return this.recordingStartedAt;
  }

  getMaxRecordingDurationMs() {
    return MAX_RECORDING_DURATION_MS;
  }

  adopt(owner, callbacks) {
    if (this.state === DICTATE_STATES.IDLE || !owner || !callbacks) return false;
    this.configure(callbacks, owner);
    return true;
  }

  /**
   * Release callbacks/resources only when the caller owns the current session.
   */
  release(owner) {
    if (this.owner !== owner) return;
    this.forceStop(owner);
    this.owner = null;
    this.onStateChange = null;
    this.onTranscript = null;
    this.onError = null;
    this.onNotice = null;
    this.onAudioLevel = null;
  }

  subscribeState(listener) {
    this.stateListeners.add(listener);
    listener(this.state);
    return () => this.stateListeners.delete(listener);
  }

  /**
   * Set state and notify listener
   */
  setState(newState) {
    this.state = newState;
    devLog('[Dictate] State changed to:', newState);
    this.onStateChange?.(newState);
    this.stateListeners.forEach(listener => listener(newState));
  }

  /**
   * Toggle recording on/off
   */
  async toggle(options = {}) {
    const {
      owner = null,
      callbacks = null,
      ...recordingOptions
    } = options;

    if (this.state === DICTATE_STATES.RECORDING) {
      // A newly visible composer may be looking at a recording started from a
      // screen still mounted in the navigation back-stack. Hand ownership to
      // the composer the user actually pressed before stopping/transcribing.
      if (owner && this.owner !== owner && callbacks) {
        this.configure(callbacks, owner);
      }
      await this.stopRecording();
      return true;
    } else if (this.state === DICTATE_STATES.IDLE) {
      // Claim the singleton synchronously before the first await. This both
      // routes callbacks to the button that was actually pressed and prevents
      // rapid repeated taps from starting concurrent AVAudioRecorder sessions.
      if (callbacks) {
        this.configure(callbacks, owner);
      } else {
        this.owner = owner;
      }
      const sessionGeneration = ++this.sessionGeneration;
      this.setState(DICTATE_STATES.STARTING);
      const started = await this.startRecording({ ...recordingOptions, sessionGeneration });
      // The owner may have unmounted while iOS was preparing AVAudioRecorder.
      // Tear down a recorder that completed after its session was released.
      if (sessionGeneration !== this.sessionGeneration) {
        return false;
      }
      if (!started && this.state === DICTATE_STATES.STARTING) {
        this.cleanup();
        this.setState(DICTATE_STATES.IDLE);
      }
      return started;
    }
    // If starting or processing, ignore repeated taps.
    return false;
  }

  /**
   * Start recording from microphone
   */
  async startRecording(options = {}) {
    if (Platform.OS === 'web') {
      return this.startWebRecording(options);
    } else {
      return this.startNativeRecording(options);
    }
  }

  /**
   * Start recording on web using MediaRecorder
   */
  async startWebRecording({ sessionGeneration = this.sessionGeneration } = {}) {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: AUDIO_SAMPLE_RATE_WEB,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      if (sessionGeneration !== this.sessionGeneration) {
        stream.getTracks().forEach(track => track.stop());
        return false;
      }
      this.stream = stream;

      // Set up audio context for silence detection
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const source = this.audioContext.createMediaStreamSource(this.stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      source.connect(this.analyser);

      // Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : 'audio/webm';
      
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
      const audioChunks = [];
      this.audioChunks = audioChunks;

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      this.mediaRecorder.onstop = async () => {
        await this.processWebRecording(sessionGeneration, audioChunks);
      };

      // A mic that is unplugged / revoked mid-recording ends the track
      // without ever firing `onstop`. Finish the session with whatever we have.
      stream.getAudioTracks().forEach((track) => {
        track.onended = () => {
          if (sessionGeneration !== this.sessionGeneration) return;
          devLog('[Dictate] Audio track ended, stopping');
          this.stopRecording();
        };
      });

      // Start recording
      this.mediaRecorder.start(100); // Collect data every 100ms
      this.recordingStartedAt = Date.now();
      this.hasHeardSound = false;
      this.lastSoundTime = this.recordingStartedAt;
      this.setState(DICTATE_STATES.RECORDING);
      
      // Initialize silence detection + duration ticker
      this.startWebSilenceDetection();
      this.startDurationTicker();
      
      // Set max duration timeout
      this.maxDurationTimeout = setTimeout(() => {
        devLog('[Dictate] Max duration reached, stopping');
        this.stopRecording();
      }, MAX_RECORDING_DURATION_MS);

      devLog('[Dictate] Web recording started');
      return true;

    } catch (error) {
      console.error('[Dictate] Failed to start web recording:', error);
      if (sessionGeneration !== this.sessionGeneration) return false;
      this.cleanup();
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        this.onError?.('Microphone permission denied. Please allow microphone access and try again.');
      } else if (error.name === 'NotFoundError') {
        this.onError?.('No microphone found. Please connect a microphone and try again.');
      } else {
        this.onError?.('Failed to start recording: ' + error.message);
      }
      return false;
    }
  }

  /**
   * Start recording on native platforms using expo-av.
   *
   * Key decisions to avoid the "recorder not prepared" error on iOS:
   *   1. Use 44100 Hz sample rate (iOS native) — 16 kHz is not reliably
   *      supported by the hardware AAC encoder on all devices.
   *   2. Use the step-by-step API (new Recording → prepareToRecordAsync →
   *      startAsync) instead of the all-in-one createAsync, so we can
   *      diagnose exactly which step fails and retry more granularly.
   *   3. Fully tear down the audio session between retries to force iOS to
   *      release any stale AVAudioSession/AVAudioRecorder references.
   */
  async startNativeRecording({ skipPermissionCheck = false, sessionGeneration = this.sessionGeneration } = {}) {
    try {
      // Request permissions unless the caller already checked in the same user gesture.
      if (!skipPermissionCheck && typeof Audio.requestPermissionsAsync === 'function') {
        const { status } = await Audio.requestPermissionsAsync();
        if (status !== 'granted') {
          this.onError?.('Microphone permission denied. Please allow microphone access in settings.');
          return;
        }
      }

      // Wait for app to be fully in the foreground before configuring audio.
      // On Android, the permission dialog puts the app into an inactive state;
      // the audio system isn't ready until the app is fully active again.
      if (AppState.currentState !== 'active') {
        devLog('[Dictate] App not in foreground (state:', AppState.currentState, '), waiting...');
        await new Promise((resolve) => {
          const subscription = AppState.addEventListener('change', (state) => {
            if (state === 'active') {
              subscription.remove();
              resolve();
            }
          });
          setTimeout(() => { subscription.remove(); resolve(); }, 5000);
        });
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // ─── Recording options ───────────────────────────────────────────
      // Uses HARDCODED numeric values because expo-av's named exports
      // (e.g. Audio.IOSOutputFormat.MPEG4AAC) can be undefined on native.
      const recordingOptions = {
        isMeteringEnabled: true,
        android: {
          extension: '.m4a',
          outputFormat: 2,    // AndroidOutputFormat.MPEG_4
          audioEncoder: 3,    // AndroidAudioEncoder.AAC
          sampleRate: AUDIO_SAMPLE_RATE_NATIVE,
          numberOfChannels: 1,
          bitRate: 128000,
        },
        ios: {
          extension: '.m4a',
          outputFormat: 1633772320, // IOSOutputFormat.MPEG4AAC ('aac ')
          audioQuality: 127,        // IOSAudioQuality.MAX — most compatible
          sampleRate: AUDIO_SAMPLE_RATE_NATIVE, // 44100 — matches iOS hardware
          numberOfChannels: 1,
          bitRate: 128000,
          linearPCMBitDepth: 16,
          linearPCMIsBigEndian: false,
          linearPCMIsFloat: false,
        },
        web: {
          mimeType: 'audio/webm',
          bitsPerSecond: 128000,
        },
      };

      // ─── Retry loop ──────────────────────────────────────────────────
      const MAX_RETRIES = 3;
      const RETRY_DELAYS = [600, 1200, 2000];
      let recording = null;
      let lastError = null;

      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        try {
          devLog(`[Dictate] Configuring audio session (attempt ${attempt + 1}/${MAX_RETRIES})...`);

          if (attempt > 0) {
            try {
              await Audio.setAudioModeAsync({ allowsRecordingIOS: false });
            } catch (_) { /* ignore */ }
            await new Promise(resolve => setTimeout(resolve, 300));
          }

          await Audio.setAudioModeAsync({
            allowsRecordingIOS: true,
            playsInSilentModeIOS: true,
            staysActiveInBackground: false,
            shouldDuckAndroid: true,
            interruptionModeIOS: INTERRUPTION_MODE_IOS_DUCK_OTHERS,
            interruptionModeAndroid: INTERRUPTION_MODE_ANDROID_DUCK_OTHERS,
          });

          // Let the audio session fully activate before touching the recorder.
          // Android needs more time than iOS after the permission dialog.
          const settleMs = Platform.OS === 'android' ? 500 : 400;
          await new Promise(resolve => setTimeout(resolve, settleMs));

          devLog(`[Dictate] Creating and starting recording (attempt ${attempt + 1}/${MAX_RETRIES})...`);

          // Try the static createAsync first (more reliable on newer expo-av)
          // then fall back to the step-by-step approach.
          let rec;
          try {
            const result = await Audio.Recording.createAsync(recordingOptions);
            rec = result.recording;
          } catch (createErr) {
            devLog(`[Dictate] createAsync failed, trying step-by-step:`, createErr.message);
            rec = new Audio.Recording();
            await rec.prepareToRecordAsync(recordingOptions);
            await rec.startAsync();
          }

          recording = rec;
          devLog('[Dictate] Recording started successfully');
          break;
        } catch (err) {
          lastError = err;
          console.error(`[Dictate] Recording attempt ${attempt + 1}/${MAX_RETRIES} failed:`, err.message);

          if (attempt < MAX_RETRIES - 1) {
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS[attempt]));
          }
        }
      }

      if (!recording) {
        throw lastError || new Error('Failed to create recording after retries');
      }
      if (sessionGeneration !== this.sessionGeneration) {
        try {
          await recording.stopAndUnloadAsync();
        } catch (_) { /* already stopped or never fully prepared */ }
        return false;
      }
      this.nativeRecording = recording;
      this.recordingStartedAt = Date.now();
      this.hasHeardSound = false;
      this.lastSoundTime = this.recordingStartedAt;

      this.setState(DICTATE_STATES.RECORDING);

      // Start native metering for waveform + silence auto-stop
      this.startNativeMetering();
      this.startDurationTicker();

      // Max duration timeout (safety net)
      this.maxDurationTimeout = setTimeout(() => {
        devLog('[Dictate] Max duration reached, stopping');
        this.stopRecording();
      }, MAX_RECORDING_DURATION_MS);

      devLog('[Dictate] Native recording started');
      return true;

    } catch (error) {
      console.error('[Dictate] Failed to start native recording:', error);
      if (sessionGeneration !== this.sessionGeneration) return false;
      this.cleanup();
      this.onError?.('Failed to start recording: ' + error.message);
      return false;
    }
  }

  /**
   * Tick elapsed recording time for UI (overlay countdown).
   */
  startDurationTicker() {
    if (this.durationTickInterval) {
      clearInterval(this.durationTickInterval);
    }
    this.durationTickInterval = setInterval(() => {
      if (this.state !== DICTATE_STATES.RECORDING || !this.recordingStartedAt) return;
      const elapsedMs = Date.now() - this.recordingStartedAt;
      this.onDurationTick?.(elapsedMs, MAX_RECORDING_DURATION_MS);
    }, 250);
  }

  /**
   * Shared silence auto-stop: once speech is detected, stop after
   * SILENCE_TIMEOUT_MS of quiet. Leading silence (before first speech)
   * does not end the recording.
   */
  noteAudioAmplitude(amplitude) {
    if (this.state !== DICTATE_STATES.RECORDING || !this.recordingStartedAt) return;

    const now = Date.now();
    const elapsed = now - this.recordingStartedAt;
    if (elapsed < MIN_RECORDING_BEFORE_SILENCE_MS) return;

    if (amplitude >= SILENCE_THRESHOLD) {
      this.hasHeardSound = true;
      this.lastSoundTime = now;
      if (this.silenceTimeout) {
        clearTimeout(this.silenceTimeout);
        this.silenceTimeout = null;
      }
      return;
    }

    if (!this.hasHeardSound || this.silenceTimeout) return;

    const silentFor = now - (this.lastSoundTime || now);
    const remaining = Math.max(0, SILENCE_TIMEOUT_MS - silentFor);
    this.silenceTimeout = setTimeout(() => {
      this.silenceTimeout = null;
      if (this.state !== DICTATE_STATES.RECORDING || !this.hasHeardSound) return;
      devLog('[Dictate] Silence detected, auto-stopping');
      this.stopRecording();
    }, remaining || 0);
  }

  /**
   * Start silence detection interval (web only).
   * Samples the time-domain signal and reduces it to one RMS amplitude per
   * tick, pushed into the same rolling history shape native metering emits.
   */
  startWebSilenceDetection() {
    const levelHistory = new Array(LEVEL_HISTORY_SIZE).fill(0);
    const dataArray = new Uint8Array(this.analyser?.fftSize || 256);

    this.silenceCheckInterval = setInterval(() => {
      if (this.state !== DICTATE_STATES.RECORDING || !this.analyser) {
        return;
      }

      this.analyser.getByteTimeDomainData(dataArray);
      let sumSquares = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const centered = (dataArray[i] - 128) / 128;
        sumSquares += centered * centered;
      }
      const rms = dataArray.length ? Math.sqrt(sumSquares / dataArray.length) : 0;

      levelHistory.shift();
      levelHistory.push(rms);
      this.onAudioLevel?.([...levelHistory]);
      this.noteAudioAmplitude(rms);
    }, SILENCE_CHECK_INTERVAL_MS);
  }

  /**
   * Start native metering interval for waveform visualization.
   * Polls recording.getStatusAsync() every 100ms and converts
   * the single dBFS metering value into an array of levels by
   * maintaining a rolling buffer of recent readings.
   */
  startNativeMetering() {
    const meterBuffer = new Array(LEVEL_HISTORY_SIZE).fill(0);

    this.nativeMeteringInterval = setInterval(async () => {
      if (this.state !== DICTATE_STATES.RECORDING || !this.nativeRecording) {
        return;
      }
      try {
        const status = await this.nativeRecording.getStatusAsync();
        if (status.isRecording && status.metering != null) {
          // Convert logarithmic dBFS to linear amplitude so the shared
          // 0-1 silence threshold has the same meaning as it does on web.
          const db = Math.max(-60, Math.min(0, status.metering));
          const amplitude = Math.pow(10, db / 20);
          // Shift buffer left and push new value
          meterBuffer.shift();
          meterBuffer.push(amplitude);
          this.onAudioLevel?.([...meterBuffer]);
          this.noteAudioAmplitude(amplitude);
        }
      } catch (_) {
        // Ignore polling errors
      }
    }, SILENCE_CHECK_INTERVAL_MS);
  }

  /**
   * Cancel recording — stops without transcribing (discards audio).
   */
  async cancel() {
    if (this.state === DICTATE_STATES.STARTING) {
      this.sessionGeneration += 1;
      this.cleanup();
      this.setState(DICTATE_STATES.IDLE);
      return;
    }
    if (this.state !== DICTATE_STATES.RECORDING) {
      return;
    }
    devLog('[Dictate] Cancelling recording...');
    this._cancelled = true;
    await this.stopRecording();
  }

  /**
   * Stop recording
   */
  async stopRecording() {
    if (this.state !== DICTATE_STATES.RECORDING) {
      return;
    }

    devLog('[Dictate] Stopping recording...');

    // Clear timeouts / meters (transcription continues after stop)
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = null;
    }
    if (this.maxDurationTimeout) {
      clearTimeout(this.maxDurationTimeout);
      this.maxDurationTimeout = null;
    }
    if (this.nativeMeteringInterval) {
      clearInterval(this.nativeMeteringInterval);
      this.nativeMeteringInterval = null;
    }
    if (this.durationTickInterval) {
      clearInterval(this.durationTickInterval);
      this.durationTickInterval = null;
    }

    if (Platform.OS === 'web') {
      // Stop silence detection
      if (this.silenceCheckInterval) {
        clearInterval(this.silenceCheckInterval);
        this.silenceCheckInterval = null;
      }

      // Stop media recorder (will trigger onstop -> processWebRecording).
      // If the recorder already went inactive on its own (track ended, device
      // lost) `onstop` has either fired or never will, so process directly
      // instead of leaving the UI stuck in the recording state.
      if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      } else {
        await this.processWebRecording();
      }
    } else {
      // Native: Stop expo-av recording
      await this.stopNativeRecording();
    }
  }

  /**
   * Stop native recording and process
   */
  async stopNativeRecording() {
    if (!this.nativeRecording) {
      this._cancelled = false;
      this.cleanup();
      this.setState(DICTATE_STATES.IDLE);
      return;
    }

    const sessionGeneration = this.sessionGeneration;
    const recording = this.nativeRecording;
    try {
      await recording.stopAndUnloadAsync();
      if (sessionGeneration !== this.sessionGeneration) return;
      const uri = recording.getURI();
      this.recordingUri = uri;
      
      devLog('[Dictate] Native recording stopped, URI:', uri);

      if (this._cancelled) {
        devLog('[Dictate] Recording was cancelled, discarding audio');
        this._cancelled = false;
        this.cleanup();
        this.setState(DICTATE_STATES.IDLE);
        return;
      }
      
      await this.processNativeRecording();
    } catch (error) {
      console.error('[Dictate] Error stopping native recording:', error);
      this.cleanup();
      this.setState(DICTATE_STATES.IDLE);
      this.onError?.('Failed to stop recording: ' + error.message);
    }
  }

  /**
   * Process the web recorded audio and send for transcription
   */
  async processWebRecording(sessionGeneration = this.sessionGeneration, audioChunks = this.audioChunks) {
    if (this._cancelled || audioChunks.length === 0) {
      devLog('[Dictate] Recording cancelled or no audio recorded');
      this._cancelled = false;
      this.cleanup();
      this.setState(DICTATE_STATES.IDLE);
      return;
    }

    this.setState(DICTATE_STATES.PROCESSING);

    try {
      // Create blob from chunks
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      
      // Check minimum size (avoid sending tiny clips)
      if (audioBlob.size < 1000) {
        devLog('[Dictate] Recording too short, ignoring');
        this.cleanup();
        this.setState(DICTATE_STATES.IDLE);
        this.onNotice?.(DICTATE_NOTICES.TOO_SHORT);
        return;
      }

      devLog('[Dictate] Sending audio for transcription, size:', audioBlob.size);

      // Send to API
      const result = await dictateApi.transcribe(audioBlob);
      if (sessionGeneration !== this.sessionGeneration) return;
      
      if (result.text && result.text.trim()) {
        devLog('[Dictate] Transcription received:', result.text);
        this.onTranscript?.(result.text.trim());
      } else {
        devLog('[Dictate] No transcription returned');
        this.onNotice?.(DICTATE_NOTICES.NO_SPEECH);
      }

    } catch (error) {
      console.error('[Dictate] Transcription failed:', error);
      if (sessionGeneration === this.sessionGeneration) {
        this.onError?.(error.message || 'Failed to transcribe audio');
      }
    } finally {
      if (sessionGeneration === this.sessionGeneration) {
        this.cleanup();
        this.setState(DICTATE_STATES.IDLE);
      }
    }
  }

  /**
   * Process the native recorded audio and send for transcription.
   *
   * On iOS: Uses the simpler POST /api/dictate/base64 endpoint which accepts
   * a JSON body with base64-encoded audio. This avoids multipart/form-data
   * serialization issues on native iOS (URLSession).
   *
   * On Android: Uses the multipart POST /api/dictate endpoint with file URI,
   * which React Native's FormData natively understands.
   */
  async processNativeRecording() {
    const sessionGeneration = this.sessionGeneration;
    const recordingUri = this.recordingUri;
    if (!recordingUri) {
      devLog('[Dictate] No recording URI');
      this.cleanup();
      this.setState(DICTATE_STATES.IDLE);
      return;
    }

    this.setState(DICTATE_STATES.PROCESSING);

    try {
      const fileInfo = await FileSystem.getInfoAsync(recordingUri);
      if (sessionGeneration !== this.sessionGeneration) return;
      
      if (!fileInfo.exists || fileInfo.size < 1000) {
        devLog('[Dictate] Recording too short or missing, ignoring');
        this.cleanup();
        this.setState(DICTATE_STATES.IDLE);
        this.onNotice?.(DICTATE_NOTICES.TOO_SHORT);
        return;
      }

      devLog('[Dictate] Sending native audio for transcription, size:', fileInfo.size);

      let result;

      if (Platform.OS === 'ios') {
        // iOS: Read file as base64 and use the simpler JSON endpoint
        const base64Audio = await FileSystem.readAsStringAsync(recordingUri, {
          encoding: FileSystem.EncodingType.Base64,
        });
        result = await dictateApi.transcribeBase64(base64Audio, 'audio/mp4');
      } else {
        // Android: Use multipart form-data with file URI
        result = await dictateApi.transcribeNative(recordingUri, 'recording.m4a', 'audio/mp4');
      }
      if (sessionGeneration !== this.sessionGeneration) return;
      
      if (result.text && result.text.trim()) {
        devLog('[Dictate] Transcription received:', result.text);
        this.onTranscript?.(result.text.trim());
      } else {
        devLog('[Dictate] No transcription returned');
        this.onNotice?.(DICTATE_NOTICES.NO_SPEECH);
      }

    } catch (error) {
      console.error('[Dictate] Native transcription failed:', error);
      if (sessionGeneration === this.sessionGeneration) {
        this.onError?.(error.message || 'Failed to transcribe audio');
      }
    } finally {
      if (sessionGeneration === this.sessionGeneration) {
        this.cleanup();
        this.setState(DICTATE_STATES.IDLE);
      }
    }
  }

  /**
   * Clean up resources
   */
  cleanup() {
    // Clear timeouts
    if (this.silenceTimeout) {
      clearTimeout(this.silenceTimeout);
      this.silenceTimeout = null;
    }
    if (this.maxDurationTimeout) {
      clearTimeout(this.maxDurationTimeout);
      this.maxDurationTimeout = null;
    }
    if (this.silenceCheckInterval) {
      clearInterval(this.silenceCheckInterval);
      this.silenceCheckInterval = null;
    }
    if (this.nativeMeteringInterval) {
      clearInterval(this.nativeMeteringInterval);
      this.nativeMeteringInterval = null;
    }
    if (this.durationTickInterval) {
      clearInterval(this.durationTickInterval);
      this.durationTickInterval = null;
    }
    this.hasHeardSound = false;
    this.lastSoundTime = null;
    this.recordingStartedAt = null;

    // Web cleanup
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch {
        // Ignore errors during cleanup
      }
    }
    this.mediaRecorder = null;
    this.audioChunks = [];

    if (this.audioContext) {
      try {
        this.audioContext.close();
      } catch {
        // Ignore errors during cleanup
      }
    }
    this.audioContext = null;
    this.analyser = null;

    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }

    // Native cleanup
    if (this.nativeRecording) {
      try {
        this.nativeRecording.stopAndUnloadAsync().catch(() => {});
      } catch {
        // Ignore errors during cleanup
      }
      this.nativeRecording = null;
    }

    // Delete the temporary recording file
    if (this.recordingUri) {
      FileSystem.deleteAsync(this.recordingUri, { idempotent: true }).catch(() => {});
      this.recordingUri = null;
    }

    // Reset audio mode on native
    if (Platform.OS !== 'web') {
      Audio.setAudioModeAsync({
        allowsRecordingIOS: false,
      }).catch(() => {});
    }

    this.lastSoundTime = null;
  }

  /**
   * Force stop everything (e.g., component unmount)
   */
  forceStop(owner = null) {
    if (owner && this.owner !== owner) return;
    this.sessionGeneration += 1;
    this.cleanup();
    this.setState(DICTATE_STATES.IDLE);
  }

  /**
   * Get current state
   */
  getState() {
    return this.state;
  }

  /**
   * Check if currently recording
   */
  isRecording() {
    return this.state === DICTATE_STATES.RECORDING;
  }

  /**
   * Check if currently processing
   */
  isProcessing() {
    return this.state === DICTATE_STATES.PROCESSING;
  }
}

// Export singleton instance
const dictateModeService = new DictateModeService();
export default dictateModeService;
